// Patent claims exercised: P-004 Claims 6-7,11-13 (trainer portal, accountless access)
import { API_BASE } from "./config";
import React, { useState } from 'react';

function TrainerPortal() {
  const [step, setStep] = useState('passcode'); // 'passcode', 'form', 'success'
  const [passcode, setPasscode] = useState('');
  const [reportInfo, setReportInfo] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState('');
  const [responseText, setResponseText] = useState('');
  const [responseCategory, setResponseCategory] = useState('general');
  const [isUrgent, setIsUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);

  // Auto-fill passcode from URL parameter
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlPasscode = urlParams.get('passcode');
    if (urlPasscode) {
      setPasscode(urlPasscode);
    }
    
    // Load response categories
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/trainer-portal/response-categories`);
      const data = await response.json();
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleValidatePasscode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/trainer-portal/validate-passcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: passcode.trim() })
      });

      const data = await response.json();

      if (data.valid) {
        setReportInfo(data);
        
        // Load trainers for this report
        const trainersResponse = await fetch(
          `${API_BASE}/api/trainer-portal/report/${data.report_id}/trainers?passcode=${passcode.trim()}`
        );
        const trainersData = await trainersResponse.json();
        setTrainers(trainersData.trainers);
        
        setStep('form');
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to validate passcode. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    if (!selectedTrainer) {
      setError('Please select your name from the trainer list');
      return;
    }
    
    if (!responseText.trim()) {
      setError('Please enter your response');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/trainer-portal/submit-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passcode: passcode.trim(),
          trainer_id: parseInt(selectedTrainer),
          response_text: responseText,
          response_category: responseCategory,
          is_urgent: isUrgent
        })
      });

      if (response.ok) {
        setStep('success');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit response');
      }
    } catch (err) {
      setError('Failed to submit response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // PASSCODE ENTRY STEP
  if (step === 'passcode') {
    return (
      <div className="tp-pageWrapper">
        <div className="tp-cardWrapper">
          <div className="tp-cardHeader">
            <h1 className="tp-portalTitle">
              Healist Trainer Portal
            </h1>
            <p className="tp-subtitle">
              Enter your one-time access code to view client fitness report
            </p>
          </div>

          <form onSubmit={handleValidatePasscode}>
            {error && (
              <div className="tp-errorMessage">
                {error}
              </div>
            )}

            <div className="tp-formFieldGroup">
              <label className="tp-accessCodeLabel">
                Access Code
              </label>
              <input
                type="text"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter 12-character code"
                required
                className="tp-passcodeInput"
              />
              <p className="tp-helperText">
                This code was provided in your email
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !passcode.trim()}
              className="tp-validateBtn" style={{ cursor: loading || !passcode.trim() ? 'not-allowed' : 'pointer', opacity: loading || !passcode.trim() ? 0.6 : 1 }}
            >
              {loading ? 'Validating...' : 'Provide Comments'}
            </button>
          </form>

          <div className="tp-importantNotesSection">
            <strong>Important:</strong>
            <ul className="tp-importantPointsList">
              <li>Access codes can only be used once</li>
              <li>The client's PDF was attached to your email</li>
              <li>This portal is for submitting your response only</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // RESPONSE FORM STEP
  if (step === 'form') {
    return (
      <div className="tp-pageWrapperForm">
        <div className="tp-cardWrapperForm">
          <div className="tp-formHeader">
            <h2 className="tp-clientNameHeading">
              Client: {reportInfo.client_name}
            </h2>
            <p className="tp-reportDateText">
              Report Date: {reportInfo.report_date}
            </p>
          </div>

          <form onSubmit={handleSubmitResponse}>
            {error && (
              <div className="tp-errorMessageForm">
                {error}
              </div>
            )}

            {/* Trainer Selection */}
            <div className="tp-trainerSelectionField">
              <label className="tp-trainerSelectionLabel">
                Select Your Name *
              </label>
              <select
                value={selectedTrainer}
                onChange={(e) => setSelectedTrainer(e.target.value)}
                required
                className="tp-trainerSelect"
              >
                <option value="">-- Select Trainer --</option>
                {trainers.map(trainer => (
                  <option key={trainer.id} value={trainer.id}>
                    {trainer.trainer_name} ({trainer.trainer_type})
                  </option>
                ))}
              </select>
            </div>

            {/* Response Category */}
            <div className="tp-responseCategoryField">
              <label className="tp-categoryLabel">
                Response Category
              </label>
              <select
                value={responseCategory}
                onChange={(e) => setResponseCategory(e.target.value)}
                className="tp-categorySelect"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Response Text */}
            <div className="tp-responseTextField">
              <label className="tp-responseLabel">
                Your Response / Recommendations *
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                required
                rows={8}
                placeholder="Enter your feedback, training recommendations, or comments for the client..."
                className="tp-responseTextarea"
              />
            </div>

            {/* Urgent Checkbox */}
            <div className="tp-urgentCheckboxField">
              <label className="tp-urgentCheckboxLabel">
                <input
                  type="checkbox"
                  checked={isUrgent}
                  onChange={(e) => setIsUrgent(e.target.checked)}
                  className="tp-urgentCheckbox"
                />
                <span className="tp-urgentCheckboxText">
                  🚨 Mark as Urgent (requires immediate client attention)
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="tp-submitBtn" style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Submitting...' : 'Submit Response'}
            </button>
          </form>

          <div className="tp-noteSection">
            <strong>Note:</strong> Your response will be securely transmitted to the client. 
            They will receive an email notification and can view your full response in their Healist account.
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS STEP
  if (step === 'success') {
    return (
      <div className="tp-pageWrapperSuccess">
        <div className="tp-cardWrapperSuccess">
          <div className="tp-successIconPlaceholder">
          </div>
          
          <h2 className="tp-successHeading">
            Response Submitted Successfully!
          </h2>
          
          <p className="tp-successMessage">
            Your feedback has been securely transmitted to {reportInfo.client_name}. 
            The client will receive an email notification and can view your response in their account.
          </p>

          {isUrgent && (
            <div className="tp-urgentNotificationSection">
              <p className="tp-urgentNotificationText">
                🚨 Urgent Response Notification Sent
              </p>
            </div>
          )}

          <div className="tp-finalNoteSection">
            <p className="tp-finalNoteText">
              <strong>Note:</strong> This access code has been used and is no longer valid. 
              You may now close this window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default TrainerPortal;